/**
 * The nav-rail tree as schmancy state — replaces the mount-time DOM climb
 * (`navigation-rail.ts` used to carry its own `ancestorRail`/`#children`
 * instance fields, wired to its peers via direct `register`/`unregister`
 * calls and a `firstUpdated` retry for the case where a parent connects a
 * beat after its child) with a single module-scoped registry plus a
 * `computed()` view over it.
 *
 * A rail's ownership (parent/depth/children) stops being a fact two
 * instances privately negotiate once, at mount, and becomes a derived
 * projection recomputed from scratch on every registration change — read it
 * whenever, it is never stale, and nothing needs a retry for a parent that
 * hasn't connected yet: the derivation simply runs again the next time
 * anything reads it after the registry changes shape.
 *
 * `discoverAscendingPeer` still does the real work of deciding ownership
 * between any two rails — a composed-tree climb is the only correct way to
 * tell "wraps" from "beside" and to pierce shadow boundaries, and state
 * can't shortcut a fact about DOM structure. What state changes is WHEN that
 * climb runs (on every registry mutation, for every currently-registered
 * rail, instead of once per rail at its own connect) and WHERE the result
 * lives (one shared, reactive map every reader observes, instead of two
 * cooperating instances' own private fields).
 *
 * One tree for the whole running app — nav rails are never sensibly scoped
 * per `<schmancy-context>` subtree, so this state is never wrapped in one;
 * `computed()`'s per-context memo path is simply unused here, and every
 * reader shares the single global memo.
 */
import { discoverAscendingPeer } from '@schmancy/mixins/discovery.service'
import { computed, state } from '@schmancy/state'
import { merge, Observable, Subject } from 'rxjs'
import type { RegisteredRail } from './context'

const NAV_RAIL_PEER_SELECTOR = 'schmancy-navigation-rail'

/**
 * Every currently-connected rail, either direction, across the whole app.
 * A rail adds itself on connect and removes itself on disconnect
 * (`registerRail`/`unregisterRail` below) — nothing else ever writes here.
 *
 * `keepOnClear: true` because this isn't per-user data `clearStates()` was
 * built to wipe on a sign-out/sign-in identity change — it is live DOM
 * bookkeeping. Clearing it mid-session would orphan every currently-mounted
 * rail (each would read back as its own root) until it happened to unmount
 * and reconnect.
 */
const registry = state<Set<RegisteredRail>>('schmancy/nav-rail-registry').memory(new Set(), { keepOnClear: true })

/** Called from a rail's own `connectedCallback`. */
export function registerRail(rail: RegisteredRail): void {
	registry.add(rail)
}

/** Called from a rail's own `disconnectedCallback`. A `Set`, so an unmount that never registered is a harmless no-op. */
export function unregisterRail(rail: RegisteredRail): void {
	registry.delete(rail)
}

type Ancestry = {
	readonly parent: RegisteredRail | null
	readonly depth: number
	readonly children: readonly RegisteredRail[]
}

/**
 * Ownership among the currently registered rails, re-derived from scratch on
 * every registration change. A dep change (a rail mounts or unmounts) marks
 * this dirty; the next read anywhere recomputes it once and every rail
 * shares that one result — see this file's own header for what does and
 * doesn't change relative to the DOM-climb-at-mount design this replaces.
 */
const navRailTree = computed<ReadonlyMap<RegisteredRail, Ancestry>>(() => deriveTree(registry.value))

function deriveTree(rails: ReadonlySet<RegisteredRail>): ReadonlyMap<RegisteredRail, Ancestry> {
	const parentOf = new Map<RegisteredRail, RegisteredRail | null>()
	for (const rail of rails) {
		const owner = discoverAscendingPeer<RegisteredRail>(NAV_RAIL_PEER_SELECTOR, rail)
		// A climb hit that isn't itself a live registrant (a same-tag element
		// mid-upgrade, a stale detached reference) reads as "no owner" rather
		// than corrupting the derived map with a key outside `rails`.
		parentOf.set(rail, owner && rails.has(owner) ? owner : null)
	}

	const childrenOf = new Map<RegisteredRail, RegisteredRail[]>()
	for (const rail of rails) childrenOf.set(rail, [])
	for (const rail of rails) {
		const parent = parentOf.get(rail) ?? null
		if (parent) childrenOf.get(parent)!.push(rail)
	}

	const depthCache = new Map<RegisteredRail, number>()
	const depthOf = (rail: RegisteredRail): number => {
		const cached = depthCache.get(rail)
		if (cached !== undefined) return cached
		const parent = parentOf.get(rail) ?? null
		const depth = parent === null ? 0 : depthOf(parent) + 1
		depthCache.set(rail, depth)
		return depth
	}

	const tree = new Map<RegisteredRail, Ancestry>()
	for (const rail of rails) {
		tree.set(rail, { parent: parentOf.get(rail) ?? null, depth: depthOf(rail), children: childrenOf.get(rail)! })
	}
	return tree
}

/**
 * Whether an operator can still reach `rail`'s rows — false for a rail inside
 * a surface the app has already made non-interactive.
 *
 * `pointer-events` because it is the one relevant CSS fact that INHERITS: a
 * surface marks itself non-interactive once, on its own root, and every rail
 * inside it carries that verdict without either side knowing about the other.
 * `<schmancy-area>` is the case this exists for — it sets `pointer-events:
 * none` on a page the moment it starts leaving, and measured in a consuming
 * admin shell that page stays mounted a further ~110ms while its rail stays
 * registered. For that whole window the shell rail it had yielded to is back
 * on screen drawing the departing surface's rows under the row the operator
 * had just chosen: rows that route through a rail whose own area is being torn
 * down, so a click on one goes nowhere. Rows nobody can act on are not
 * navigation.
 *
 * Separate from visibility rather than folded into it: the departing rail is
 * perfectly on screen, at full measure, for several of those frames, and
 * `navRailIsVisible` answers which rail carries navigation, not whose column
 * a set of rows belongs in.
 */
export function navRailIsReachable(rail: RegisteredRail): boolean {
	return getComputedStyle(rail).pointerEvents !== 'none'
}

/**
 * `rail`'s own place in the derived tree — `null` for one that has left it.
 *
 * An unreachable rail that is on screen in its own right leaves whole: no
 * parent, no depth, and nobody's child. Both halves matter and they are the
 * same fact read from either end — the column above stops drawing its rows,
 * and the rail itself, no longer owned by anyone, keeps drawing its own column
 * for as long as it is on screen instead of collapsing to nothing the frame
 * its ancestor reappears. That is what lets a departing surface leave with its
 * own motion rather than snapping shut first.
 *
 * A rail that is on screen in NOTHING of its own stays where it is, because
 * there is nothing there for it to leave with. Absorbed, it draws one hidden
 * slot and generates no box at all (`navigation-rail.ts`'s own
 * `:host([absorbed])`), which is exactly what `navRailIsVisible` reads as
 * false — and orphaning it would hand it a column or a strip AND the app's
 * puck on the way out, popping navigation onto a dying surface that never
 * carried any of it. Observed in Chromium on the settings legal shape: the
 * absorbed strip took a box, two tabs and a puck the moment its page stopped
 * taking clicks.
 */
function ancestry(rail: RegisteredRail): Ancestry | null {
	if (!navRailIsReachable(rail) && navRailIsVisible(rail)) return null
	return navRailTree.get().get(rail) ?? null
}

/** `depth` for a rail the tree doesn't (yet) carry defaults to 0 — the same "no ancestor" reading a lone root gets. */
export function navRailDepth(rail: RegisteredRail): number {
	return ancestry(rail)?.depth ?? 0
}

/**
 * The children a drill or cascade drawn from `rail` may descend into, in
 * registration order — empty when none.
 *
 * Two children are left out, and both are the same invariant read from the
 * parent's side: exactly one surface draws any given rail's rows.
 *
 * A child the operator can no longer reach is a stale level whose own surface
 * is being torn down — `navRailIsReachable` above holds that half, and it
 * holds it for every caller, because a row nobody can click is navigation
 * nowhere.
 *
 * A child with no visible ancestor is the top of everything anyone can see
 * and draws its own rows — but only a CASCADE is a second surface drawing
 * them, so only a cascade leaves it out. A consumer hiding a shell rail for a
 * focus context left that hidden rail's cascade still holding the focused
 * rail's whole level: invisible while it lasted, and painted at full measure
 * on the very first frame of the shell rail's return, before any observer
 * could correct it. The walk stops at that first hop, so a deeper level under
 * the same hidden cascade never arrives either.
 *
 * Under the default (`NestedRails`) a child draws its own column BESIDE this
 * one and this rail's column draws none of its rows, so there is no second
 * surface to be exactly one of — and below `md` there is no column at all,
 * where the puck's drill sheet is the only thing reading this. Excluding the
 * child there took the drill's only route into it: a consumer whose shell
 * column leaves the layout below `md` (`hidden md:flex` on the box holding
 * the rail) has no visible ancestor for anything nested under it, so every
 * nested level disappeared from the sheet and its destinations became
 * unreachable on a phone — the one width where the sheet IS navigation.
 */
export function navRailChildren(rail: RegisteredRail): readonly RegisteredRail[] {
	const drawnByThisCascade = rail.nestedRails === 'cascade'
	return (
		ancestry(rail)?.children.filter(
			child => navRailIsReachable(child) && (!drawnByThisCascade || navRailVisibleAncestor(child) !== null),
		) ?? []
	)
}

/**
 * The level `rail`'s currently active item carries — `null` when it carries
 * none.
 *
 * The most recently registered child rather than the first: for as long as a
 * navigation takes to tear the outgoing surface down, both that surface's
 * rail and the incoming one are registered here, and the level the newly
 * active item revealed is always the one that registered last. Reading the
 * first attributes the level the operator just left to the row they just
 * chose.
 *
 * The outgoing rail is usually gone from the candidate set before that
 * tie-break is even needed — `navRailChildren` above drops it the moment its
 * own surface stops taking clicks — but the ordering still decides the case
 * where two levels are genuinely reachable at once.
 */
export function navRailActiveChild(rail: RegisteredRail): RegisteredRail | null {
	return navRailChildren(rail).at(-1) ?? null
}

/**
 * The rail that owns `rail`, `null` for a root — the handle
 * `navRailVisibleAncestor` climbs, which `depth > 0` (the same predicate,
 * minus the element) cannot supply.
 */
export function navRailParent(rail: RegisteredRail): RegisteredRail | null {
	return ancestry(rail)?.parent ?? null
}

/**
 * Whether `rail` is on screen right now — the one thing that decides which
 * rail carries the mobile puck, since a rail its consumer has hidden takes
 * the puck off screen with it.
 *
 * `checkVisibility` rather than a geometry read, measured in Chromium
 * against the four cases that matter: a rail hidden with `display:none` (or
 * `hidden`, or a `display:none` anywhere above it) answers false; a rail
 * below `md` — where the column is `hidden` and the puck carries navigation
 * instead — measures 0×0 and answers true; a rail that has not rendered yet
 * also measures 0×0 and answers true, so a rail below it evaluating
 * mid-mount never briefly unabsorbs itself and no verdict depends on which
 * rail rendered first. A width test reads those last two as hidden, which is
 * both wrong and a mount-order race. A rail clipped away by a zero-width
 * ancestor is the one hiding mechanism this cannot see — it still renders
 * its box, and so measures exactly like the below-`md` rail whose
 * navigation is fully on screen.
 */
export function navRailIsVisible(rail: RegisteredRail): boolean {
	return rail.checkVisibility({ contentVisibilityAuto: true, visibilityProperty: true })
}

/**
 * The nearest rail above `rail` that is on screen, `null` when none is —
 * which makes `rail` the top of the tree anyone can actually see, and so the
 * level the mobile puck starts its cascade at. Registration order is no
 * answer to that: a registered depth-0 rail its consumer has hidden takes
 * the puck off screen with it, and the rail below has to carry it instead or
 * the surface has no navigation at all below `md`.
 */
export function navRailVisibleAncestor(rail: RegisteredRail): RegisteredRail | null {
	let above = navRailParent(rail)
	while (above !== null && !navRailIsVisible(above)) above = navRailParent(above)
	return above
}

/**
 * Whether `rail`'s rows are already on screen inside a column ABOVE it — true
 * exactly when some rail anyone can currently see above it asked for the
 * cascade (`NestedRails`), false for every rail under the default.
 *
 * Read from the ancestor and not from `rail` itself, because absorption is
 * something done TO a rail: a rail never knows whether the column above it
 * intends to draw its rows, and a mode it declared about itself could not
 * answer for the levels beneath it either.
 *
 * The whole visible chain is walked rather than only the nearest link, so a
 * cascade two levels up still owns a grandchild whose own parent happens to
 * be left at the default — every level between them is absorbed by that same
 * cascade, and a grandchild drawing a column of its own beside rows the top
 * column is already drawing is the "same choices twice on one screen" defect
 * the whole rule exists to prevent.
 *
 * A rail with no visible ancestor is never absorbed, whichever mode anything
 * above it declares — which is what keeps a surface whose shell rail its
 * consumer has hidden (an event console taking over the frame) drawing its
 * own rows instead of losing navigation with it.
 */
export function navRailAbsorbed(rail: RegisteredRail): boolean {
	for (let above = navRailVisibleAncestor(rail); above !== null; above = navRailVisibleAncestor(above)) {
		if (above.nestedRails === 'cascade') return true
	}
	return false
}

const selectionMoved$ = new Subject<void>()

/** Called by a rail whose own selection moved — see `navRailTreeChanged$`. */
export function navRailSelectionMoved(): void {
	selectionMoved$.next()
}

const visibilityMoved$ = new Subject<void>()

/**
 * Called by a rail that just went on or off screen — see `navRailTreeChanged$`.
 * A rail announces only that its own verdict moved; every other rail then
 * re-reads `navRailIsVisible` for itself rather than trusting a pushed value,
 * so no rail's picture depends on another rail's bookkeeping.
 */
export function navRailVisibilityMoved(): void {
	visibilityMoved$.next()
}

/**
 * Emits whenever the drill tree the puck opens has moved: a rail mounted or
 * unmounted anywhere in the app, any rail's own selection changed, or a rail
 * went on or off screen without unmounting. No two of those imply each other
 * — routing between two sibling destinations that each carry no nested rail
 * moves a level's active item with the registered set untouched, a lazily
 * mounted rail arrives with every selection already where it was, and a rail
 * a route hides stays registered with its whole subtree's selection intact
 * while the rail below it has to start carrying the puck.
 */
export const navRailTreeChanged$: Observable<unknown> = merge(registry.$, selectionMoved$, visibilityMoved$)
